/**
 * Finnish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Finnish at a mathematician's register. **Warrant → «Peruste»**
 * (ground/justification — flagged; Finnish has no settled Toulmin term). Finnish counts take
 * the partitive: «1 rajoite» but «2 rajoitetta». W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other, so `frameActive`
 * declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a
 * presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const FI_CATALOG = {
    locale: 'fi',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Tutkimus',
        [P2.sectionClaims]: 'Väitteet',
        [P2.sectionIntegrity]: 'Eheys',
        [P2.axisWarrantLabel]: 'Peruste',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empiirinen',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Määritelmä} interp {Tulkinta} testable {Testattavissa} tested {Testattu} refuted {Kumottu} program {Tutkimusohjelma} philos {Filosofinen} other {Tuntematon}}',
        [P2.axisModalLabel]: 'Modaalinen',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Vahvuustaso {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Tutkittavissa',
        [P2.stateRefuted]: 'Kumottu',
        [P2.stateOutside]: 'Soveltamisalan ulkopuolella',
        [P2.verdictConflict]: 'Ristiriidassa kohteen {constraints} kanssa',
        [P2.verdictOutside]: 'Vaatii {missing}',
        [P2.lossRegisterPlain]: 'Yksinkertaistettu yleiskielelle — osa tarkkuudesta jää pois.',
        [P2.lossRegisterKid]: 'Yksinkertaistettu nuoremmille lukijoille — monet yksityiskohdat jäävät pois.',
        [P2.lossProfileDetail]: 'Täysi profiili Peruste / Empiirinen / Modaalinen on tiivistetty tähän.',
        [P2.lossConflictDetail]: 'Ristiriidan tiedot on lyhennetty tässä näkymässä.',
        [P2.laneVerified]: 'Vahvistettu',
        [P2.laneUnverified]: 'Vahvistamaton',
        [P2.laneConflict]: 'Ristiriita todistettu',
        [P2.constraintLogic]: 'Logiikka',
        [P2.constraintLightBends]: 'Valo taipuu gravitaatiokentässä',
        [P2.constraintParallelPostulate]: 'Paralleeliaksiooma',
        [P2.claimScalarGravity]: 'Skalaarinen gravitaatioteoria',
        [P2.claimNonEuclidean]: 'Epäeuklidinen geometria',
        [P2.claimSubstrateSr]: 'Suppean suhteellisuusteorian substraattitulkinta',
        [P2.edgeExtObservation]: 'Ulkoinen havainto',
        [P2.edgeResourceSample]: 'Jaettu laskentaresurssi',
        [P2.edgeHeuristicSample]: 'Heuristinen ohjaus',
        [P2.edgeSharedSample]: 'Yhteinen perusta',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Rajoitteet',
        [P2.sectionEdges]: 'Kaaret',
        [P2.sectionFrame]: 'Kehys',
        [P2.sectionExposure]: 'Altistus',
        [P2.statusAdoptedFormal]: 'Hyväksytty (muodollinen)',
        [P2.statusAdoptedInterpretive]: 'Hyväksytty (tulkinnallinen)',
        [P2.statusSuspended]: 'Keskeytetty',
        [P2.edgeEffectConstraint]: 'Muodostaa rajoitteen (voi kumota)',
        [P2.edgeEffectResource]: 'Resurssi — ei vaikutusta ratkaisuihin',
        [P2.edgeEffectHeuristic]: 'Heuristiikka — ei vaikutusta ratkaisuihin',
        [P2.edgeEffectShared]: 'Yhteinen perusta — ei vaikutusta ratkaisuihin',
        [P2.frameActive]: '{count, plural, =0 {Ei aktiivisia rajoitteita} one {# aktiivinen rajoite} other {# aktiivista rajoitetta}}',
        [P2.exposureNone]: 'Ei tulkinnallisia riippuvuuksia',
        [P2.exposureDep]: 'Riippuu kohteesta {id}',
        [P2.laneTrackMachine]: 'Kone',
        [P2.laneTrackExpert]: 'Asiantuntija',
        [P2.laneTrackCommunity]: 'Yhteisö',
        [P2.expertAudited]: 'Auditoitu',
        [P2.expertInProgress]: 'Käynnissä',
        [P2.expertNotReviewed]: 'Ei tarkistettu',
        [P2.communityFlags]: 'Merkintöjä on',
        [P2.communityNone]: 'Ei merkintöjä',
        [P2.controlChallenge]: 'Kiistä',
        [P2.controlActivate]: 'Aktivoi',
        [P2.controlMakeInterpretive]: 'Tee tulkinnalliseksi',
        [P2.controlImportEdge]: 'Tuo kaari',
        [P2.controlPromote]: 'Ylennä',
        [P2.controlUndo]: 'Kumoa',
        [P2.controlRedo]: 'Tee uudelleen',
        [P2.controlRegister]: 'Rekisteröi',
        [P2.promoteRejected]: 'Ylennys hylätty',
        [P2.promoteAccepted]: 'Ylennys hyväksytty',
        [P2.promoteReasonRefutationTerminal]: 'Todistus ei voi parantaa kumoamista.',
        [P2.promoteReasonKeystone]: 'Kulmakiviylennys vaatii empiiristä tukea.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empiirinen tyyppi ei ole kiinnitetty.',
        [P2.promoteReasonModalNotFixed]: 'Modaalinen aste ei ole kiinnitetty.',
        [P2.promoteReasonWarrantNotImproved]: 'Perusteaste ei ole parantunut.',
        [P2.evidenceComputation]: "{engine} laski {op} = {result} kohteessa {config}",
        [P2.lossNumericRounding]: "Tulos pyöristetty {dp} desimaaliin",
    },
};
//# sourceMappingURL=fi.js.map